/**
 * Droits des rôles internes.
 *
 * Ces contrôles sont d'ERGONOMIE, pas de sécurité : ils évitent de proposer une action qui
 * finira en 403 et de laisser croire à un opérateur usine qu'il peut suspendre le compte d'un
 * client. L'autorisation qui fait foi est celle du serveur, revérifiée à chaque appel — un
 * back-office reste une application cliente, et une application cliente ne protège rien.
 *
 * Découpage retenu (à confirmer au cadrage du 2e livrable) :
 *  - STAFF_OPERATEUR : chaîne de production. Il alimente le stock et imprime les QR codes.
 *  - STAFF_SUPPORT   : SAV. Il diagnostique, désassocie, change le statut d'un appareil.
 *  - STAFF_ADMIN     : tout, plus ce qui touche aux comptes clients (suspension) et à l'audit.
 */

import { RoleInterne } from '@genviz/shared/enums';
import type { OperateurInterne } from '@genviz/shared/dto';

export const Capacite = {
  /** Voir le tableau de bord et la carte de supervision. */
  SUPERVISER: 'SUPERVISER',
  /** Consulter le stock et la fiche d'un appareil. */
  CONSULTER_STOCK: 'CONSULTER_STOCK',
  /** Importer un lot d'appareils et générer les QR codes. */
  IMPORTER_STOCK: 'IMPORTER_STOCK',
  /** Déplacer un appareil dans son cycle de vie (SAV, hors service, retour en stock). */
  CHANGER_STATUT_STOCK: 'CHANGER_STATUT_STOCK',
  /** Lire le diagnostic technique : trames brutes, commandes, divergences de configuration. */
  DIAGNOSTIQUER: 'DIAGNOSTIQUER',
  /** Désassocier de force un appareil de son exploitation (litige, échange de module). */
  DESASSOCIER: 'DESASSOCIER',
  /** Régler l'étalonnage de la tension d'un appareil (coefficient kV par LSB de pic). */
  ETALONNER: 'ETALONNER',
  /** Consulter les exploitations et leurs utilisateurs. */
  CONSULTER_EXPLOITATIONS: 'CONSULTER_EXPLOITATIONS',
  /** Suspendre ou réactiver une exploitation, désactiver un compte utilisateur. */
  SUSPENDRE_COMPTE: 'SUSPENDRE_COMPTE',
  /** Consulter le journal d'audit. */
  CONSULTER_AUDIT: 'CONSULTER_AUDIT',
  /** Consulter la vue commerciale des clients et la consommation de donnees. */
  CONSULTER_CLIENTS: 'CONSULTER_CLIENTS',
  /** Souscrire, suspendre ou resilier un abonnement SMS. */
  GERER_ABONNEMENTS: 'GERER_ABONNEMENTS',
  /** Traiter la file du support : repondre, changer statut et priorite. */
  TRAITER_SAV: 'TRAITER_SAV',
  /** Interroger l'assistant Claude (historique du développement, lecture du code). */
  INTERROGER_ASSISTANT: 'INTERROGER_ASSISTANT',
} as const;
export type Capacite = (typeof Capacite)[keyof typeof Capacite];

const TOUTES: Capacite[] = Object.values(Capacite);

export const CAPACITES_PAR_ROLE: Readonly<Record<RoleInterne, readonly Capacite[]>> = {
  [RoleInterne.STAFF_OPERATEUR]: [
    Capacite.SUPERVISER,
    Capacite.CONSULTER_STOCK,
    Capacite.IMPORTER_STOCK,
    Capacite.CHANGER_STATUT_STOCK,
    Capacite.DIAGNOSTIQUER,
    Capacite.ETALONNER,
  ],
  [RoleInterne.STAFF_SUPPORT]: [
    Capacite.SUPERVISER,
    Capacite.CONSULTER_STOCK,
    Capacite.CHANGER_STATUT_STOCK,
    Capacite.DIAGNOSTIQUER,
    Capacite.DESASSOCIER,
    Capacite.ETALONNER,
    Capacite.CONSULTER_EXPLOITATIONS,
    Capacite.CONSULTER_AUDIT,
    // Le support voit la fiche commerciale d'un client -- il en a besoin pour repondre a
    // « pourquoi je ne recois pas mes SMS » -- et traite la file du SAV. Il ne SOUSCRIT ni ne
    // resilie : engager un client sur un abonnement payant releve d'un role distinct.
    Capacite.CONSULTER_CLIENTS,
    Capacite.TRAITER_SAV,
  ],
  [RoleInterne.STAFF_ADMIN]: TOUTES,
};

/** Vrai si l'opérateur détient la capacité. Un opérateur absent ne peut rien. */
export function peut(operateur: OperateurInterne | null, capacite: Capacite): boolean {
  if (!operateur) return false;
  return CAPACITES_PAR_ROLE[operateur.role].includes(capacite);
}

/** Message affiché à la place d'une action indisponible. */
export function motifIndisponibilite(operateur: OperateurInterne | null): string {
  if (!operateur) return 'Session expirée.';
  return 'Votre rôle ne permet pas cette action.';
}
